/**
 * @file useAccess.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-08-27
 *
 * Access context: the signed-in user's badge, permissions and read-only state,
 * available anywhere without threading `user` through every prop.
 *
 * Most screens already take a `user` prop and can keep using the plain helpers
 * in utils/permissions.js. This exists for the places where that prop does not
 * reach — modals mounted through EntityModalProvider, shared table cells, and
 * anything several levels below the screen component.
 *
 *   const { can, canWrite, readOnly, badge } = useAccess();
 *   <button disabled={!canWrite('orders.edit')}>…</button>
 *
 * `readOnly` is the one most call sites want: a view-only account should see
 * every action button in its normal slot, disabled with a reason, rather than
 * have the buttons vanish and the row change shape. That is the same rule the
 * project already applies to inapplicable row actions.
 */

import React, { createContext, useContext, useMemo } from 'react';
import {
  hasPermission, hasAnyPermission, badgeOf, isReadOnly, canWrite, xeroRequirement
} from '../utils/permissions';

const AccessContext = createContext(null);

export function AccessProvider({ user, children }) {
  const value = useMemo(() => ({
    user,
    badge: badgeOf(user),
    badgeLabel: user?.badge_label || (badgeOf(user) === 'REP' ? 'Rep' : badgeOf(user) === 'ADMIN' ? 'Admin' : 'User'),
    roles: user?.roles || [],
    isAdmin: user?.is_admin === true,
    readOnly: isReadOnly(user),
    repId: user?.rep_id || null,
    repName: user?.rep_name || null,
    xero: xeroRequirement(user),
    can: (key) => hasPermission(user, key),
    canAny: (keys) => hasAnyPermission(user, keys),
    canWrite: (key) => canWrite(user, key)
  }), [user]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

/**
 * Safe outside a provider — returns a "no access" shape rather than throwing,
 * so a component can be rendered in isolation (a test, a storybook page)
 * without the whole app around it.
 */
export function useAccess() {
  const ctx = useContext(AccessContext);
  if (ctx) return ctx;
  return {
    user: null, badge: 'USER', badgeLabel: 'User', roles: [], isAdmin: false,
    readOnly: false, repId: null, repName: null, xero: 'required',
    can: () => false, canAny: () => false, canWrite: () => false
  };
}

/**
 * Shorthand for the commonest case. Returns { readOnly, reason } so a title
 * attribute can explain a disabled control instead of leaving it inert and
 * unexplained.
 */
export function useReadOnly() {
  const { readOnly, badgeLabel } = useAccess();
  return {
    readOnly,
    reason: readOnly ? `${badgeLabel} accounts have view-only access` : null
  };
}

export default useAccess;
